import React, { useState } from "react";
import { useAuth } from "../lib/AuthContext";
import { sendPasswordResetEmail } from "firebase/auth";
import { auth } from "../lib/firebase";

const GIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24">
    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
  </svg>
);

const BrandMark = ({ size = 64, radius = 20, font = 30 }) => (
  <div style={{ width: size, height: size, background: "linear-gradient(135deg,#0A0F1E,#1B2A5B)", borderRadius: radius, display: "flex", alignItems: "center", justifyContent: "center", boxShadow: "0 12px 30px rgba(10,15,30,.35)" }}>
    <span style={{ fontSize: font, color: "#fff" }}>✦</span>
  </div>
);

const FEATURES = [
  ["⚡", "Instant transfers", "Send money to anyone on XyloPay in seconds. Transfers up to ₹2,000 are completely free."],
  ["⏳", "Hold & release", "Lock funds on a timer for deals with people you don't know yet. Release early, or let it auto-release."],
  ["🔐", "6-digit PIN", "Every payment needs your personal PIN. Your money never moves without you."],
  ["📧", "Real-time alerts", "Email notifications for every payment — sent, received, held, and released."],
  ["🛡️", "Protected by design", "Encrypted connections, automatic sign-out after 15 minutes idle, and full transaction history."],
  ["🇮🇳", "Made for India", "Built for how India pays — simple, fast, and transparent pricing with no hidden charges."],
];

const STEPS = [
  ["1", "Create your account", "Sign up with Google or email in under a minute. Free forever."],
  ["2", "Add money", "Link your bank and top up your XyloPay wallet securely."],
  ["3", "Send or hold", "Pay instantly, or place a hold when you want extra protection on a deal."],
];

const FAQS = [
  ["Is my money safe with XyloPay?", "Yes. Every payment is PIN-protected, connections are encrypted, and you get an email alert for every movement of money. Held funds stay locked until the timer ends or you release them."],
  ["What does XyloPay cost?", "Transfers up to ₹2,000 are completely free. Above ₹2,000, a flat ₹6 service fee applies. No hidden charges, ever."],
  ["How does Hold & Release work?", "When you place a hold, the money is locked and the recipient is notified immediately — but they receive it only when the timer expires or you release it early. You can cancel a hold from the Activity screen."],
  ["Who can I send money to?", "Anyone with a XyloPay account. Search by name, email, or their XyloPay ID right from the Send screen."],
];

export default function AuthPage() {
  const { loginWithGoogle, signUpWithEmail, loginWithEmail } = useAuth();
  const [view, setView]       = useState("welcome");
  const [form, setForm]       = useState({ name:"", email:"", password:"", confirm:"" });
  const [err, setErr]         = useState("");
  const [loading, setLoading] = useState(false);
  const [resetSent, setResetSent] = useState(false);
  const F = k => e => setForm(f => ({ ...f, [k]: e.target.value }));

  const handleForgotPassword = async () => {
    if (!form.email) return setErr("Enter your email address first.");
    setErr(""); setLoading(true);
    try {
      await sendPasswordResetEmail(auth, form.email);
      setResetSent(true);
    } catch(e) {
      setErr(e.code === "auth/user-not-found" ? "No account found with this email." : "Failed to send reset email. Try again.");
    } finally { setLoading(false); }
  };

  const handleGoogle = async () => {
    setErr(""); setLoading(true);
    try { await loginWithGoogle(); }
    catch (e) {
      setErr(
        e.code === "auth/popup-closed-by-user" ? "Sign-in cancelled." :
        e.code === "auth/popup-blocked" ? "Popup blocked. Please allow popups for this site." :
        "Google sign-in failed. Try again."
      );
    } finally { setLoading(false); }
  };

  const handleSignup = async () => {
    if (!form.name||!form.email||!form.password) return setErr("All fields are required.");
    if (!form.email.includes("@")) return setErr("Enter a valid email address.");
    if (form.password.length < 6) return setErr("Password must be at least 6 characters.");
    if (form.password !== form.confirm) return setErr("Passwords do not match.");
    setErr(""); setLoading(true);
    try { await signUpWithEmail(form.name, form.email, form.password); }
    catch (e) {
      setErr(
        e.code === "auth/email-already-in-use" ? "Account already exists. Sign in instead." :
        "Sign up failed. Try again."
      );
    } finally { setLoading(false); }
  };

  const handleLogin = async () => {
    if (!form.email||!form.password) return setErr("Enter your email and password.");
    setErr(""); setLoading(true);
    try { await loginWithEmail(form.email, form.password); }
    catch (e) {
      setErr(
        e.code === "auth/wrong-password" || e.code === "auth/user-not-found" ? "Incorrect email or password." :
        e.code === "auth/too-many-requests" ? "Too many attempts. Try again later." :
        "Sign in failed. Try again."
      );
    } finally { setLoading(false); }
  };

  /* ── PUBLIC LANDING PAGE ─────────────────────────────────── */
  if (view === "welcome") return (
    <div style={{ fontFamily: "'Outfit',sans-serif", color: "#fff", background: "#0A0F1E" }}>

      {/* HERO */}
      <div className="hero-grad" style={{ padding: "34px 24px 0", overflow: "hidden" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 44 }} className="fade-in">
          <div style={{ width: 38, height: 38, background: "linear-gradient(135deg,#0A0F1E,#1B2A5B)", border: "1px solid rgba(255,255,255,.14)", borderRadius: 12, display: "flex", alignItems: "center", justifyContent: "center" }}>
            <span style={{ fontSize: 18, color: "#fff" }}>✦</span>
          </div>
          <p style={{ fontWeight: 900, fontSize: 21, letterSpacing: -0.8 }}>xylo<span style={{ color: "#7EE2B8", fontWeight: 600 }}>pay</span></p>
        </div>

        <div style={{ textAlign: "center", paddingBottom: 8 }} className="fade-in">
          <span className="eyebrow">✦ &nbsp;INDIA'S ESCROW-SAFE WALLET</span>
          <h1 style={{ fontSize: 42, fontWeight: 900, letterSpacing: -2, lineHeight: 1.08, margin: "22px 0 16px" }}>
            Money that moves<br/>on <span style={{ background: "linear-gradient(90deg,#34D399,#7EE2B8)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>your terms.</span>
          </h1>
          <p style={{ fontSize: 15, color: "#A7B1C9", lineHeight: 1.7, maxWidth: 330, margin: "0 auto" }}>
            Send money instantly — or lock it on hold until you're sure. You stay in control, always.
          </p>
          <div style={{ display: "flex", flexDirection: "column", gap: 10, maxWidth: 340, margin: "28px auto 0" }}>
            <button className="btn btn-primary" onClick={()=>setView("signup")}
              style={{ padding: "17px", fontSize: 16 }}>
              Create free account <span>→</span>
            </button>
            <button className="btn" onClick={()=>setView("login")}
              style={{ padding: "16px", fontSize: 15, fontWeight: 700, color: "#D5DCEB", background: "rgba(255,255,255,.06)", border: "1px solid rgba(255,255,255,.12)", borderRadius: 999 }}>
              Sign in
            </button>
          </div>
          <p style={{ fontSize: 11, color: "#5B6B8C", marginTop: 14 }}>Free forever · No card required · 2-minute setup</p>
        </div>

        {/* Floating wallet card visual */}
        <div style={{ display: "flex", justifyContent: "center", padding: "34px 0 0" }} className="fade-in">
          <div className="float-y" style={{
            width: "100%", maxWidth: 340, borderRadius: 24, padding: "22px",
            background: "linear-gradient(135deg,#101A33 0%,#1B2A5B 100%)",
            border: "1px solid rgba(255,255,255,.12)",
            boxShadow: "0 30px 70px rgba(0,0,0,.5)"
          }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
              <span style={{ fontSize: 10, letterSpacing: 2, color: "#7EE2B8", fontWeight: 800 }}>XYLOPAY BALANCE</span>
              <span style={{ fontSize: 16, color: "#fff" }}>✦</span>
            </div>
            <p style={{ fontSize: 38, fontWeight: 900, letterSpacing: -1.5 }}>₹24,500<span style={{ fontSize: 18, color: "#7EE2B8" }}>.00</span></p>
            <div style={{ display: "flex", gap: 8, marginTop: 16 }}>
              <div style={{ flex: 1, background: "rgba(255,255,255,.07)", borderRadius: 12, padding: "10px", textAlign: "center" }}>
                <p style={{ fontSize: 15 }}>⚡</p><p style={{ fontSize: 10, color: "#A7B1C9", marginTop: 4, fontWeight: 700 }}>Instant</p>
              </div>
              <div style={{ flex: 1, background: "rgba(255,255,255,.07)", borderRadius: 12, padding: "10px", textAlign: "center" }}>
                <p style={{ fontSize: 15 }}>⏳</p><p style={{ fontSize: 10, color: "#A7B1C9", marginTop: 4, fontWeight: 700 }}>On hold</p>
              </div>
              <div style={{ flex: 1, background: "rgba(255,255,255,.07)", borderRadius: 12, padding: "10px", textAlign: "center" }}>
                <p style={{ fontSize: 15 }}>🔐</p><p style={{ fontSize: 10, color: "#A7B1C9", marginTop: 4, fontWeight: 700 }}>PIN locked</p>
              </div>
            </div>
          </div>
        </div>
        <div style={{ height: 44 }} />
      </div>

      {/* FEATURES */}
      <div style={{ background: "#0A0F1E", padding: "44px 24px 8px" }}>
        <p style={{ fontSize: 11, fontWeight: 800, letterSpacing: 2, color: "#7EE2B8", textAlign: "center", marginBottom: 10 }}>WHY XYLOPAY</p>
        <h2 style={{ fontSize: 26, fontWeight: 900, letterSpacing: -1, textAlign: "center", marginBottom: 24 }}>Built for how India pays</h2>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
          {FEATURES.map(([ic, t, d], i) => (
            <div key={i} className="feature-card">
              <p style={{ fontSize: 24, marginBottom: 10 }}>{ic}</p>
              <p style={{ fontWeight: 800, fontSize: 14, marginBottom: 6 }}>{t}</p>
              <p style={{ fontSize: 12, color: "#A7B1C9", lineHeight: 1.6 }}>{d}</p>
            </div>
          ))}
        </div>
      </div>

      {/* HOW IT WORKS */}
      <div style={{ background: "#0D1526", padding: "44px 24px", marginTop: 36 }}>
        <p style={{ fontSize: 11, fontWeight: 800, letterSpacing: 2, color: "#7EE2B8", textAlign: "center", marginBottom: 10 }}>HOW IT WORKS</p>
        <h2 style={{ fontSize: 26, fontWeight: 900, letterSpacing: -1, textAlign: "center", marginBottom: 26 }}>Up and running in minutes</h2>
        {STEPS.map(([n, t, d], i) => (
          <div key={i} style={{ display: "flex", gap: 16, marginBottom: i < 2 ? 20 : 0 }}>
            <div style={{ width: 40, height: 40, borderRadius: "50%", background: "linear-gradient(135deg,#0E9F6E,#34D399)", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 900, fontSize: 16, flexShrink: 0, boxShadow: "0 8px 20px rgba(14,159,110,.35)" }}>{n}</div>
            <div>
              <p style={{ fontWeight: 800, fontSize: 15, marginBottom: 4 }}>{t}</p>
              <p style={{ fontSize: 13, color: "#A7B1C9", lineHeight: 1.6 }}>{d}</p>
            </div>
          </div>
        ))}
      </div>

      {/* PRICING */}
      <div style={{ padding: "44px 24px" }}>
        <div style={{ background: "linear-gradient(135deg,#0E9F6E 0%,#0B7A55 100%)", borderRadius: 26, padding: "30px 24px", textAlign: "center", boxShadow: "0 24px 60px rgba(14,159,110,.3)" }}>
          <p style={{ fontSize: 11, fontWeight: 800, letterSpacing: 2, color: "rgba(255,255,255,.75)", marginBottom: 12 }}>SIMPLE PRICING</p>
          <p style={{ fontSize: 34, fontWeight: 900, letterSpacing: -1.5 }}>Free up to ₹2,000</p>
          <p style={{ fontSize: 14, color: "rgba(255,255,255,.85)", marginTop: 8, lineHeight: 1.6 }}>Above ₹2,000, a flat ₹6 per transfer.<br/>No hidden charges. Ever.</p>
        </div>
      </div>

      {/* FAQ */}
      <div style={{ padding: "8px 24px 44px" }}>
        <h2 style={{ fontSize: 24, fontWeight: 900, letterSpacing: -1, textAlign: "center", marginBottom: 22 }}>Questions, answered</h2>
        {FAQS.map(([q, a], i) => (
          <div key={i} style={{ background: "rgba(255,255,255,.04)", border: "1px solid rgba(255,255,255,.09)", borderRadius: 18, padding: "18px", marginBottom: 10 }}>
            <p style={{ fontWeight: 800, fontSize: 14, marginBottom: 8 }}>{q}</p>
            <p style={{ fontSize: 13, color: "#A7B1C9", lineHeight: 1.7 }}>{a}</p>
          </div>
        ))}
      </div>

      {/* FINAL CTA */}
      <div style={{ padding: "8px 24px 50px", textAlign: "center" }}>
        <BrandMark size={56} radius={18} font={26} />
        <h2 style={{ fontSize: 26, fontWeight: 900, letterSpacing: -1, margin: "18px 0 10px" }}>Take control of your money.</h2>
        <p style={{ fontSize: 14, color: "#A7B1C9", marginBottom: 24 }}>Join XyloPay free — send your first payment in minutes.</p>
        <button className="btn btn-primary" onClick={()=>setView("signup")} style={{ padding: "17px 40px", fontSize: 16, width: "100%", maxWidth: 340 }}>
          Get started <span>→</span>
        </button>
        <p style={{ fontSize: 11, color: "#5B6B8C", marginTop: 26, lineHeight: 1.8 }}>
          By continuing you agree to our Terms of Service and Privacy Policy.<br/>
          Questions? <span style={{ color: "#7EE2B8", fontWeight: 700 }}>ask999k@gmail.com</span>
        </p>
        <p style={{ fontSize: 11, color: "#3D4763", marginTop: 14 }}>© 2026 XyloPay · Secure Digital Wallet</p>
      </div>
    </div>
  );

  /* ── AUTH FORMS ──────────────────────────────────────────── */
  const isLogin = view === "login";
  return (
    <div style={{ minHeight: "100vh", background: "linear-gradient(180deg,#0A0F1E 0%,#0D1730 130px,#FFFFFF 130px)", display: "flex", flexDirection: "column", alignItems: "center", fontFamily: "'Outfit',sans-serif", color: "#0A0F1E", padding: "0 0 40px" }}>
      <div style={{ width: "100%", maxWidth: 400, padding: "40px 24px 0" }} className="fade-in">
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 30 }}>
          <button className="btn" onClick={()=>setView("welcome")}
            style={{ background: "rgba(255,255,255,.9)", border: "1px solid #E5EAF3", borderRadius: 12, width: 40, height: 40, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 17, color: "#0A0F1E", boxShadow: "0 4px 12px rgba(10,15,30,.08)" }}>←</button>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <div style={{ width: 30, height: 30, background: "linear-gradient(135deg,#0A0F1E,#1B2A5B)", borderRadius: 9, display: "flex", alignItems: "center", justifyContent: "center" }}>
              <span style={{ fontSize: 14, color: "#fff" }}>✦</span>
            </div>
            <p style={{ fontWeight: 900, fontSize: 17, letterSpacing: -0.6, color: "#fff" }}>xylo<span style={{ color: "#7EE2B8", fontWeight: 600 }}>pay</span></p>
          </div>
          <div style={{ width: 40 }} />
        </div>

        <div className="card" style={{ padding: "30px 26px", borderRadius: 26, boxShadow: "0 24px 60px rgba(10,15,30,.12)" }}>
          <p style={{ fontWeight: 900, fontSize: 24, letterSpacing: -0.8, marginBottom: 6 }}>{isLogin ? "Welcome back" : "Create your account"}</p>
          <p style={{ fontSize: 14, color: "#5B6B8C", marginBottom: 24 }}>{isLogin ? "Sign in to your wallet" : "Free forever · 2-minute setup"}</p>

          <button className="btn" onClick={handleGoogle} disabled={loading}
            style={{ width: "100%", background: "#fff", color: "#0A0F1E", border: "1.5px solid #E5EAF3", borderRadius: 14, padding: "14px", fontSize: 15, fontWeight: 700, display: "flex", alignItems: "center", justifyContent: "center", gap: 10, marginBottom: 18, boxShadow: "0 4px 14px rgba(10,15,30,.06)" }}>
            {loading ? <span className="spinner"/> : <GIcon/>}
            {loading ? "Opening Google..." : "Continue with Google"}
          </button>

          <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 18 }}>
            <div style={{ flex: 1, height: 1, background: "#EEF1F7" }}/>
            <p style={{ fontSize: 11, color: "#98A2B8", fontWeight: 700, letterSpacing: 1 }}>OR WITH EMAIL</p>
            <div style={{ flex: 1, height: 1, background: "#EEF1F7" }}/>
          </div>

          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            {!isLogin && <input className="input" placeholder="Full name" value={form.name} onChange={F("name")}/>}
            <input className="input" placeholder="Email address" type="email" value={form.email} onChange={F("email")}/>
            <input className="input" placeholder="Password" type="password" value={form.password} onChange={F("password")}/>
            {!isLogin && <input className="input" placeholder="Confirm password" type="password" value={form.confirm} onChange={F("confirm")}/>}
            {err && <div className="error-box">{err}</div>}
            {resetSent && <div className="ok-box">✓ Password reset email sent! Check your inbox.</div>}
            <button className="btn btn-primary" onClick={isLogin ? handleLogin : handleSignup} disabled={loading}
              style={{ padding: "16px", fontSize: 15, marginTop: 4, opacity: loading ? .75 : 1 }}>
              {loading && <span className="spinner" style={{ borderTopColor: "#fff", borderColor: "rgba(255,255,255,.25)" }}/>}
              {loading ? "Please wait..." : isLogin ? "Sign in →" : "Create account →"}
            </button>
            {isLogin && (
              <button className="btn" onClick={handleForgotPassword} disabled={loading}
                style={{ background: "transparent", border: "none", color: "#5B6B8C", fontSize: 13, fontWeight: 600, cursor: "pointer", textAlign: "center", marginTop: 2 }}>
                Forgot password?
              </button>
            )}
          </div>

          <p style={{ textAlign: "center", fontSize: 13, color: "#98A2B8", marginTop: 20 }}>
            {isLogin ? "New to XyloPay? " : "Already have an account? "}
            <button className="btn" onClick={()=>{ setErr(""); setView(isLogin?"signup":"login"); }}
              style={{ background: "transparent", border: "none", color: "#0A0F1E", fontWeight: 800, fontSize: 13, cursor: "pointer", textDecoration: "underline" }}>
              {isLogin ? "Create account" : "Sign in"}
            </button>
          </p>
        </div>

        <div style={{ display: "flex", gap: 8, alignItems: "center", justifyContent: "center", marginTop: 26 }}>
          <span style={{ fontSize: 14 }}>🔒</span>
          <p style={{ fontSize: 11, color: "#98A2B8", fontWeight: 600, letterSpacing: .4 }}>256-bit encrypted · PIN protected · Fraud monitored</p>
        </div>
      </div>
    </div>
  );
}
